import { motion } from "framer-motion";
import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import IosShareIcon from "@mui/icons-material/IosShare";
import LinkIcon from "@mui/icons-material/Link";
// Lazy-load feed images for better LCP and bandwidth (replaces former Next.js Image)
import LazyImage from "./LazyImage";
const Image = ({ src, alt, className, unoptimized, width, height, ...props }: any) => (
  <LazyImage src={src} alt={alt} className={className} {...props} />
);

import { convertToShamsiRelative } from "../utils/dateUtils";
import { extractVideoUrl, hasValidVideo } from "../utils/videoUtils";
import { renderTextWithNewlines } from "../utils/textUtils";
import AuthModal from "./AuthModal";
import ErrorToast from "./ErrorToast";
import SuccessToast from "./SuccessToast";
import { useOptimisticLike } from "../hooks/useLikes";
import { useCreateComment, useDeleteComment } from "../hooks/useComments";
import { useDeletePost } from "../hooks/usePosts";
import { useAuth } from "../lib/auth";
import { useTranslation } from "../hooks/useTranslation";
import { useCreateStory, getApiUrl } from "../lib/api";
import { shareToInstagramStory } from "../utils/instagramStoryShare";
import VerificationBadge from "./VerificationBadge";
import { useVideoPlayer } from "../lib/videoPlayerContext";
import { formatNumber } from "../utils/formatNumber";
import LikesModal from "./LikesModal";
import CommentSection from "./CommentSection";
import PollDisplay from "./PollDisplay";
import { useVoteOnPostPoll, useVoteOnPostPollByHash } from "../lib/api";

type Tag = {
  id: number;
  postId: number;
  name: string;
};

type PostProps = {
  id: string;
  img: string;
  video?: string | null;
  caption: string;
  userId: number;
  user?: {
    id: number;
    username: string;
    name: string;
    image: string;
    verified?: boolean;
  };
  comments?: any[];
  tags?: Tag[];
  createdAt?: string; // Add creation time
  showAllComments?: boolean; // New prop to control comment display
  hashCode?: string; // Hash code for hash-based posts
  like?: any[]; // Likes array (from main posts API – no per-post likes request)
  isLikedByCurrentUser?: boolean; // From API when authenticated
  postType?: string; // Post type
  isHashBased?: boolean; // Whether post is hash-based
  poll?: any; // Poll data
};

const Post: React.FC<PostProps> = ({
  id,
  img,
  video,
  caption,
  userId,
  user,
  comments = [],
  createdAt,
  showAllComments = false,
  hashCode,
  like = [],
  isLikedByCurrentUser: isLikedByCurrentUserProp,
  postType,
  poll,
  isHashBased,
}) => {
  // Early validation - don't render if post ID is invalid
  if (!id || id === 'null' || id === 'undefined') {
    return null;
  }

  const { t } = useTranslation();
  const { isAuthenticated, user: currentUser, token } = useAuth();
  const navigate = useNavigate();
  const votePollMutation = useVoteOnPostPoll();
  const votePollByHashMutation = useVoteOnPostPollByHash();

  // Normalize poll for PollDisplay: ensure options is an array with id, optionText, voteCount, percentage
  const normalizedPoll = useMemo(() => {
    if (!poll || postType !== "poll") return null;
    const options = Array.isArray(poll.options) ? poll.options : (poll.Options && Array.isArray(poll.Options) ? poll.Options : []);
    return {
      id: poll.id,
      question: poll.question || "",
      options: options.map((o: any) => ({
        id: o.id,
        optionText: o.optionText ?? o.option_text ?? "",
        voteCount: Number(o.voteCount ?? o.vote_count ?? 0),
        percentage: Number(o.percentage ?? 0),
      })),
      totalVotes: Number(poll.totalVotes ?? poll.total_votes ?? 0),
      userVote: poll.userVote ?? poll.user_vote ?? null,
    };
  }, [poll, postType]);

  const [comment, setComment] = useState("");
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState<"like" | "comment">("like");
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<"post" | "comment">("post");
  const [deleteTargetId, setDeleteTargetId] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  /** Track last tap time for iOS double-tap (dblclick is unreliable on iOS). */
  const lastTapTimeRef = useRef(0);
  const DOUBLE_TAP_MS = 400;
  const { playingVideoId, registerVideo, playVideo, clearPlayingVideo } = useVideoPlayer();
  const playingVideoIdRef = useRef(playingVideoId);
  useEffect(() => {
    playingVideoIdRef.current = playingVideoId;
  }, [playingVideoId]);

  // Use comments from props (which come from React Query cache)
  const displayComments = Array.isArray(comments) ? comments : [];

  // Likes come from the main posts API – no per-post likes request
  const likes = Array.isArray(like) ? like : [];

  // Use API flag when present, otherwise derive from likes array and current user
  const hasLikes = useMemo(() => {
    if (isLikedByCurrentUserProp === true) return true;
    if (!isAuthenticated || !currentUser || !likes.length) return false;
    return likes.some((l: any) => {
      const likeUserId = l.userId ?? l.user_id ?? l.user?.id;
      return likeUserId != null && Number(likeUserId) === Number(currentUser.id);
    });
  }, [isAuthenticated, currentUser, likes, isLikedByCurrentUserProp]);

  const {
    displayLiked,
    displayCount,
    likePost: toggleLike,
    isPending: isLikePending,
  } = useOptimisticLike(String(id), currentUser?.id, likes.length, hasLikes, {
    onError: (error) => {
      setErrorMessage(error?.message || t('errors.likeFailed'));
      setShowError(true);
    },
  });
  const createCommentMutation = useCreateComment();
  const deleteCommentMutation = useDeleteComment();
  const deletePostMutation = useDeletePost();
  const createStoryMutation = useCreateStory();
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [shareStoryLoading, setShareStoryLoading] = useState(false);

  const sendComment = async (e: any) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setAuthAction("comment");
      setShowAuthModal(true);
      return;
    }

    if (comment.trim()) {
      const commentContent = comment.trim();
      setComment(""); // Clear input immediately for better UX

      try {
        // Send comment to server - optimistic updates are handled in the mutation
        await createCommentMutation.mutateAsync({
          post_id: String(id),
          content: commentContent,
          user_id: Number(currentUser?.id)
        });
      } catch (error: any) {
        
        // Restore comment text on error
        setComment(commentContent);
        
        // Show error message
        setErrorMessage(error.message || t('errors.commentFailed'));
        setShowError(true);
      }
    }
  };

  const likePost = () => {
    if (!isAuthenticated) {
      setAuthAction("like");
      setShowAuthModal(true);
      return;
    }
    if (!id || id === "null" || id === "undefined") {
      setErrorMessage(t('errors.postNotFound'));
      setShowError(true);
      return;
    }
    if (!displayLiked) {
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 2000);
    }
    toggleLike();
  };

  /** Handle double-tap on touch devices (iOS often doesn't fire dblclick). */
  const handleDoubleTapTouch = useCallback(
    (e: React.TouchEvent) => {
      const now = Date.now();
      if (now - lastTapTimeRef.current <= DOUBLE_TAP_MS) {
        e.preventDefault();
        lastTapTimeRef.current = 0;
        likePost();
      } else {
        lastTapTimeRef.current = now;
      }
    },
    [likePost]
  );

  const handleCopyLink = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const base = window.location.origin;
    const postUrl = hashCode ? `${base}/p/${hashCode}` : `${base}/post/${id}`;
    navigator.clipboard.writeText(postUrl).then(
      () => {
        setSuccessMessage(t('common.linkCopied'));
        setShowSuccess(true);
      },
      () => {
        setErrorMessage(t('errors.copyLinkFailed'));
        setShowError(true);
      }
    );
  };

  const handleShareToStory = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (shareStoryLoading) return;
    setShareStoryLoading(true);
    try {
      const base = window.location.origin;
      const postUrl = hashCode ? `${base}/p/${hashCode}` : `${base}/post/${id}`;
      let profilePictureUrl = user?.image
        ? user.image.startsWith("http")
          ? user.image
          : `${getApiUrl()}${user.image.replace(/^\//, "/")}`
        : undefined;
      if (profilePictureUrl && typeof window !== "undefined") {
        try {
          const urlOrigin = new URL(profilePictureUrl).origin;
          if (urlOrigin !== window.location.origin) {
            const apiBase = (getApiUrl() || "").replace(/\/$/, "");
            profilePictureUrl = `${apiBase}/api/proxy-image?url=${encodeURIComponent(profilePictureUrl)}`;
          }
        } catch {
          // keep original if URL parsing fails
        }
      }
      const result = await shareToInstagramStory({
        text: caption || "",
        authorName: user?.name || user?.username,
        profilePictureUrl,
        isVerified: user?.verified,
        url: postUrl,
      });
      if (result.fallbackDownload) {
        setSuccessMessage(t('common.storyImageSaved'));
        setShowSuccess(true);
      }
      if (result.error) {
        setErrorMessage(result.error);
        setShowError(true);
      }
    } finally {
      setShareStoryLoading(false);
    }
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const closeErrorToast = () => {
    setShowError(false);
    setErrorMessage("");
  };

  const closeSuccessToast = () => {
    setShowSuccess(false);
    setSuccessMessage("");
  };

  const handleDeletePost = () => {
    setDeleteType("post");
    setDeleteTargetId(String(id));
    setShowDeleteConfirm(true);
  };

  const handleDeleteComment = (commentId: string) => {
    setDeleteType("comment");
    setDeleteTargetId(commentId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === "post") {
        await deletePostMutation.mutateAsync(deleteTargetId);
        setSuccessMessage(t('common.postDeleted'));
        setShowSuccess(true);
        // Only navigate if we're on a single post page, otherwise stay on current page
        if (window.location.pathname.includes('/post/')) {
          setTimeout(() => navigate('/'), 1000); // Delay navigation to show success message
        }
      } else {
        await deleteCommentMutation.mutateAsync(deleteTargetId);
        setSuccessMessage(t('common.commentDeleted'));
        setShowSuccess(true);
      }
      setShowDeleteConfirm(false);
    } catch (error: any) {
      setErrorMessage(error.message || t('errors.deleteFailed'));
      setShowError(true);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteType("post");
    setDeleteTargetId("");
  };

  // Check if we have video or image (needed for useEffect hooks)
  const videoUrl = extractVideoUrl(video);
  const hasVideo = hasValidVideo(video);
  const hasImage = Boolean(img && img !== "");
  /** SEO: short alt for post media (caption or fallback) */
  const postMediaAlt = (caption?.trim().slice(0, 100) || t('post.viewPost')) + (caption && caption.length > 100 ? "…" : "");

  // Register video with context and handle pause callback
  useEffect(() => {
    if (!hasVideo) return;

    const pauseCallback = () => {
      setIsVideoPlaying(false);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };

    const unregister = registerVideo(String(id), pauseCallback);
    return unregister;
  }, [hasVideo, id, registerVideo]);

  // Intersection Observer: only tell context which video to play (single source of truth)
  // Do NOT set isVideoPlaying here — sync effect below does that from playingVideoId
  useEffect(() => {
    if (!hasVideo || !videoContainerRef.current) return;

    const thisId = String(id);
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          // Left view: clear context if this was the playing video (avoids multi-video)
          if (playingVideoIdRef.current === thisId) {
            clearPlayingVideo();
          }
          continue;
        }
        // In view: only one video "wins" by being most visible (use 0.8 so only one is active)
        if (entry.intersectionRatio >= 0.5) {
          playVideo(thisId);
          break; // one entry per observed element; still only call playVideo once per post
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: [0, 0.5, 1],
      rootMargin: '0px',
    });

    observer.observe(videoContainerRef.current);
    return () => observer.disconnect();
  }, [hasVideo, id, playVideo, clearPlayingVideo]);

  // Single source of truth: sync with context and drive play/pause from playingVideoId
  useEffect(() => {
    const thisId = String(id);
    const isActive = playingVideoId === thisId;

    if (isActive) {
      setIsVideoPlaying(true);
      const el = videoRef.current;
      if (el && el.paused) {
        const playWhenReady = () => {
          el.play().catch(() => {});
        };
        if (el.readyState >= 2) {
          playWhenReady();
        } else {
          el.addEventListener('canplay', playWhenReady, { once: true });
          return () => el.removeEventListener('canplay', playWhenReady);
        }
      }
    } else {
      setIsVideoPlaying(false);
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    }
  }, [playingVideoId, id]);

  // Keep local pause in sync when user pauses via controls
  useEffect(() => {
    if (!isVideoPlaying && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [isVideoPlaying]);

  // Pause video when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      setIsVideoPlaying(false);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <>
      <ErrorToast
        message={errorMessage}
        isVisible={showError}
        onClose={closeErrorToast}
      />
      <SuccessToast
        message={successMessage}
        isVisible={showSuccess}
        onClose={closeSuccessToast}
      />
      <motion.article
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="bg-white dark:bg-gray-900 mt-3 mb-5 dark:border-gray-700 rounded-sm relative"
        aria-label={caption?.trim().slice(0, 80) || t('post.viewPost')}
      >
        {/* Post Header - User Info */}
        {user && (
          <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <Link
              to={`/${user.username}`}
              className="flex items-center flex-1 min-w-0 hover:opacity-90 transition-opacity"
            >
              {user.image ? (
                <LazyImage
                  src={user.image}
                  alt={user.name || user.username}
                  className="w-10 h-10 rounded-full object-cover me-3 flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center me-3 flex-shrink-0">
                  <span className="text-white text-sm font-medium">
                    {(user.name || user.username)?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                    {user.name || user.username}
                  </span>
                  <VerificationBadge verified={user.verified} size="sm" />
                </div>
                {user.name && user.username && (
                  <span className="text-gray-500 dark:text-gray-400 text-xs truncate">
                    {user.username}
                  </span>
                )}
              </div>
            </Link>
            
            {/* Post options dropdown — for poll: only show if owner (Delete); no Copy link for poll */}
            {user && (postType !== "poll" || (isAuthenticated && currentUser && currentUser.username === user.username)) && (
              <div className="relative" ref={dropdownRef}>
                <motion.button 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-600 dark:text-gray-400"
                  title="گزینه‌ها"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </motion.button>
                
                {/* Dropdown Menu */}
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.1 }}
                      className="absolute end-0 mt-2 w-36 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                    >
                      {postType !== "poll" && (
                      <motion.button
                        whileHover={{ backgroundColor: "rgba(59, 130, 246, 0.1)" }}
                        onClick={() => {
                          setShowDropdown(false);
                          handleCopyLink();
                        }}
                        className="w-full px-4 py-2 text-start text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-start gap-2"
                      >
                        <LinkIcon className="w-4 h-4" />
                        {t('post.copyLink')}
                      </motion.button>
                      )}
                      {isAuthenticated && currentUser && currentUser.username === user.username && (
                        <motion.button
                          whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                          onClick={() => {
                            setShowDropdown(false);
                            handleDeletePost();
                          }}
                          disabled={deletePostMutation.isPending}
                          className="w-full px-4 py-2 text-right text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-start"
                        >
                          {deletePostMutation.isPending ? (
                            <>
                              <svg className="w-4 h-4 animate-spin me-2" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              در حال حذف...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              حذف پست
                            </>
                          )}
                        </motion.button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
        
        {hasVideo ? (
          <div ref={videoContainerRef} className="relative w-full overflow-hidden">
            <AnimatePresence>
              {showLikeAnimation && (
                <>
                  {/* Main heart animation */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 0 }}
                    animate={{ 
                      opacity: [0, 1, 1, 0], 
                      scale: [0.5, 1.2, 1, 0.8],
                      y: [0, -20, -40, -60]
                    }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ 
                      duration: 2,
                      times: [0, 0.2, 0.8, 1],
                      ease: "easeOut"
                    }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                  >
                    <div className="bg-red-500 rounded-full p-4 shadow-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="white"
                        className="w-12 h-12"
                      >
                        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                      </svg>
                    </div>
                  </motion.div>

                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ 
                        opacity: 0, 
                        scale: 0,
                        x: 0,
                        y: 0
                      }}
                      animate={{ 
                        opacity: [0, 1, 0],
                        scale: [0, 1, 0.5],
                        x: [(Math.random() - 0.5) * 100, (Math.random() - 0.5) * 200],
                        y: [0, -100 - Math.random() * 100]
                      }}
                      transition={{ 
                        duration: 1.5 + Math.random() * 0.5,
                        delay: Math.random() * 0.3,
                        ease: "easeOut"
                      }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="red"
                        className="w-6 h-6"
                      >
                        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                      </svg>
                    </motion.div>
                  ))}
                </>
              )}
            </AnimatePresence>
            
            <video
              key={`video-${id}`}
              ref={videoRef}
              src={videoUrl}
              poster={img || '/default-video-cover.jpg'}
              className="object-cover w-full bg-black"
              controls
              muted
              loop
              playsInline
              preload="metadata"
              onPlay={() => {
                playVideo(String(id));
                setIsVideoPlaying(true);
              }}
              onPause={() => {
                // If this video is paused and it's not the active one, hide overlay
                // If user manually pauses the active video, also hide overlay
                if (playingVideoId !== String(id)) {
                  setIsVideoPlaying(false);
                } else if (videoRef.current?.paused) {
                  // User paused via controls - show overlay but keep as active in context
                  setIsVideoPlaying(false);
                }
              }}
              onEnded={() => {
                setIsVideoPlaying(false);
              }}
            />
            <div
              className={`absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-opacity duration-200 ${
                isVideoPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              onClick={() => {
                playVideo(String(id));
                setIsVideoPlaying(true);
              }}
              onDoubleClick={postType === "poll" ? undefined : (e) => {
                e.stopPropagation();
                likePost();
              }}
              onTouchEnd={postType === "poll" ? undefined : handleDoubleTapTouch}
              aria-hidden={isVideoPlaying}
            >
              <Image
                src={img || '/default-video-cover.jpg'}
                alt={postMediaAlt}
                width={600}
                height={400}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                unoptimized
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="white"
                  className="w-16 h-16 drop-shadow-lg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347c-.75.412-1.667-.13-1.667-.986V5.653Z"
                  />
                </svg>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded pointer-events-none">
                کلیک برای پخش
              </div>
            </div>
          </div>
        ) : hasImage ? (
          <div className="relative w-full overflow-hidden">
            {/* Like Animation Overlay for Image */}
            <AnimatePresence>
              {showLikeAnimation && (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 0 }}
                    animate={{ 
                      opacity: [0, 1, 1, 0], 
                      scale: [0.5, 1.2, 1, 0.8],
                      y: [0, -20, -40, -60]
                    }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ 
                      duration: 2,
                      times: [0, 0.2, 0.8, 1],
                      ease: "easeOut"
                    }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                  >
                    <div className="bg-red-500 rounded-full p-4 shadow-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="white"
                        className="w-12 h-12"
                      >
                        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                      </svg>
                    </div>
                  </motion.div>

                  {/* Floating mini hearts */}
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ 
                        opacity: 0, 
                        scale: 0,
                        x: 0,
                        y: 0
                      }}
                      animate={{ 
                        opacity: [0, 1, 0],
                        scale: [0, 1, 0.5],
                        x: [(Math.random() - 0.5) * 100, (Math.random() - 0.5) * 200],
                        y: [0, -100 - Math.random() * 100]
                      }}
                      transition={{ 
                        duration: 1.5 + Math.random() * 0.5,
                        delay: Math.random() * 0.3,
                        ease: "easeOut"
                      }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="red"
                        className="w-6 h-6"
                      >
                        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                      </svg>
                    </motion.div>
                  ))}
                </>
              )}
            </AnimatePresence>
            
            <Image
              src={img}
              alt={postMediaAlt}
              width={600}
              height={400}
              className="object-cover w-full cursor-pointer"
              unoptimized
              onDoubleClick={postType === "poll" ? undefined : likePost}
              onTouchEnd={postType === "poll" ? undefined : handleDoubleTapTouch}
            />
          </div>
        ) : postType !== "poll" ? (
          <div className="p-5">
            <div className="text-start text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
              {renderTextWithNewlines(caption)}
            </div>
          </div>
        ) : null}
        
        <div className="flex mb-3 justify-between items-center px-2 me-0 pt-4">
            {/* Like and share buttons — hidden for poll posts */}
            {postType !== "poll" && (
            <div className="flex">
              <div 
                className={`flex items-center gap-0.5 py-1.5 px-3 rounded-full transition-colors duration-200 touch-manipulation ${
                  displayLiked
                    ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    : "text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400"
                }`}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={likePost}
                  disabled={isLikePending}
                  className="flex items-center justify-center p-0 border-0 bg-transparent disabled:opacity-100 disabled:cursor-default"
                  aria-label={displayLiked ? t('post.unlike') : t('post.like')}
                >
                  {displayLiked ? (
                    <FavoriteIcon className="w-5 h-5" />
                  ) : (
                    <FavoriteBorderIcon className="w-5 h-5" />
                  )}
                </motion.button>
                {displayCount > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLikesModal(true);
                    }}
                    className={`text-[13px] me-0 font-medium cursor-pointer select-none p-0 border-0 bg-transparent ${
                      displayLiked ? "text-red-500" : "text-gray-500 dark:text-gray-400"
                    }`}
                    aria-label={t('post.viewLikes')}
                  >
                    ({formatNumber(displayCount)})
                  </motion.button>
                )}
              </div>
              {/* Share to Instagram Story */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleShareToStory}
                disabled={shareStoryLoading}
                className="group flex items-center gap-0 px-3 mt-[-5px] py-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200 disabled:opacity-60"
                aria-label={t('post.shareToInstagramStory')}
              >
                <IosShareIcon className="w-5 h-5" />
              </motion.button>
            </div>
            )}
            {createdAt && (
              <span className="text-gray-500 me-2 dark:text-gray-400 text-[10px]">
                {convertToShamsiRelative(createdAt)}
              </span>
            )}
        </div>
        {postType !== "text" && caption != null && caption.trim() !== "" ? (
          <p className="p-5 text-start text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
            {renderTextWithNewlines(caption)}
          </p>
        ) : null}
        {postType === "poll" && normalizedPoll && normalizedPoll.options.length > 0 ? (
          <div className="px-4 pb-4">
            <PollDisplay
              poll={normalizedPoll}
              isAuthenticated={isAuthenticated}
              onVote={async (optionId: number) => {
                if (isHashBased && hashCode) {
                  await votePollByHashMutation.mutateAsync({ hashCode, optionId });
                } else if (poll?.id) {
                  await votePollMutation.mutateAsync({ pollId: poll.id, optionId });
                }
              }}
            />
          </div>
        ) : null}
        <CommentSection
          comments={displayComments}
          showAllComments={showAllComments || commentsExpanded}
          initialVisibleCount={3}
          currentUserId={currentUser?.id}
          isAuthenticated={isAuthenticated}
          deleteCommentMutation={deleteCommentMutation}
          deleteTargetId={deleteTargetId}
          onDeleteComment={handleDeleteComment}
          onViewMoreClick={() => setCommentsExpanded(true)}
          className="me-0"
        />

          <form className="flex items-center p-4 border-t border-gray-200 dark:border-gray-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-7 text-gray-600 dark:text-gray-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
              />
            </svg>

            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('post.addComment')}
              className="border-none text-xs flex-1 focus:ring-0 outline-none text-start bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
            {createCommentMutation.isPending ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 animate-spin text-blue-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
            ) : (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="submit"
                onClick={sendComment}
                className="font-semibold text-xs text-blue-400 me-2"
              >
                {t('post.sendComment')}
              </motion.button>
            )}
          </form>
      </motion.article>

      <AuthModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        action={authAction}
      />

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {deleteType === "post" ? t('common.deletePost') : t('common.deleteComment')}
                </h3>
                
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {deleteType === "post" ? t('common.confirmDeletePost') : t('common.confirmDeleteComment')}
                </p>
                
                <div className="flex justify-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common.cancel')}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDelete}
                    disabled={deletePostMutation.isPending || deleteCommentMutation.isPending}
                    className="px-4 me-2 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {(deletePostMutation.isPending || deleteCommentMutation.isPending) ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -me-1 me-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('common.deleting')}
                      </div>
                    ) : (
                      t('common.delete')
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <LikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        likes={likes}
      />
    </>
  );
};

export default React.memo(Post);
